import { useGetAccountQuery } from '@/services/accounts';
import { useGetQuotesQuery } from '@/services/quotes';
import { Box, TextInput } from '@palmetto/palmetto-components';
import { ChangeEvent, FocusEvent, useCallback, useEffect, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { QuoteStatus, RequirementFlagSettingsType } from 'types';

import { CommonFormSection } from '../ProductionValueCheck/Form';
import { FlagBox } from '../QueueFlags/FlagBox';

interface InstallContractInfoProps {
  isSubmitting: boolean;
  isLoading: boolean;
  userOnlyHasReadPermissions: boolean;
  isPackageEditable?: boolean;
}

const calculatePercentageDifference = (quoteCost: number, workOrderCost: number) => {
  if (quoteCost === 0) return 0;
  return ((workOrderCost - quoteCost) / quoteCost) * 100;
};

const calculateDecimalPlaces = (percentageDiff: number) => {
  if (Math.abs(percentageDiff) < 0.01 && percentageDiff !== 0) return 3;
  return 2;
};

export const InstallContractInfo = ({
  isSubmitting,
  isLoading,
  userOnlyHasReadPermissions,
  isPackageEditable = true,
}: InstallContractInfoProps) => {
  const { id } = useParams<{ id: any }>();
  const { data: account } = useGetAccountQuery(id);
  const { data: quotes } = useGetQuotesQuery(
    { id, programType: account?.programType },
    { skip: !account?.programType },
  );

  const [percentageDifference, setPercentageDifference] = useState<number>(0);
  const [costDiscrepancy, setCostDiscrepancy] = useState<boolean>(false);

  const { control, setValue } = useFormContext<any>();
  const workOrderTotalPrice = useWatch({ control, name: 'workOrderTotalPrice' });

  const activeQuote = quotes?.find((quote: any) => quote.status === QuoteStatus.active);
  const quoteTotalSystemCost = activeQuote?.totalSystemCost || 0;

  useEffect(() => {
    if (workOrderTotalPrice > 0) {
      const percentageDiff = calculatePercentageDifference(quoteTotalSystemCost, workOrderTotalPrice);
      setCostDiscrepancy(true);
      setPercentageDifference(percentageDiff);
    } else {
      setCostDiscrepancy(false);
    }
  }, [activeQuote, quoteTotalSystemCost, workOrderTotalPrice]);

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>, fieldOnBlur: () => void) => {
      const value = parseFloat(event.target.value);
      setValue('workOrderTotalPrice', isNaN(value) ? 0 : value, { shouldDirty: true });
      if (value > 0 && value !== quoteTotalSystemCost) {
        setCostDiscrepancy(true);
        setPercentageDifference(calculatePercentageDifference(quoteTotalSystemCost, value));
      } else {
        setCostDiscrepancy(false);
      }
      fieldOnBlur();
    },
    [setValue, quoteTotalSystemCost],
  );

  const handleFocus = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      if (event.target.value === '0') {
        setValue('workOrderTotalPrice', '', { shouldDirty: true });
      }
    },
    [setValue],
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, fieldOnChange: (value: number) => void) => {
      const value = parseFloat(event.target.value);
      fieldOnChange(value);
    },
    [],
  );

  return (
    <Box width={'100'}>
      <CommonFormSection title="Install Contract Total Price">
        <Box direction="column" gap="lg" width="xl">
          <Controller
            name="workOrderTotalPrice"
            control={control}
            render={({ field }) => (
              <TextInput
                id="workOrderTotalPrice"
                name="workOrderTotalPrice"
                label=""
                hideLabel
                value={field.value ?? ''}
                type="number"
                prefix="$"
                width="2xl"
                isDisabled={isSubmitting || isLoading || userOnlyHasReadPermissions || !isPackageEditable}
                onFocus={handleFocus}
                onBlur={(e) => handleBlur(e, field.onBlur)}
                onChange={(e) => handleChange(e as ChangeEvent<HTMLInputElement>, field.onChange)}
              />
            )}
          />
        </Box>
      </CommonFormSection>
      {costDiscrepancy && (
        <CommonFormSection title="Cost Difference">
          <Box fontWeight={'medium'} fontSize={'lg'}>
            {percentageDifference.toFixed(calculateDecimalPlaces(percentageDifference))}%
          </Box>
        </CommonFormSection>
      )}
      <CommonFormSection title=" ">
        <FlagBox
          dataKey="System Cost Discrepancy"
          flagType={RequirementFlagSettingsType.systemCostDiscrepancyHVAC}
        />
      </CommonFormSection>
    </Box>
  );
};
